import React, { useState, useMemo, useEffect } from 'react';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';

function PlanList({ onAddPlan, onEditPlan }) {
    const { hasPermission } = usePermissionContext();
        const [rows, setRows] = useState([]);
    const [exportType, setExportType] = useState('');
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exportColumns, setExportColumns] = useState({
        planId: true,
        planName: true,
        planDurationDays: true,
        planPrice: true,
        planDiscount: true,
        finalPrice: true,
        planDescription: true,
        isActive: true
    });

    const columnLabels = {
        planId: 'Plan ID',
        planName: 'Name',
        planDurationDays: 'Duration',
        planPrice: 'Price (₹)',
        planDiscount: 'Discount (%)',
        finalPrice: 'Final Price (₹)',
        planDescription: 'Description',
        isActive: 'Status'
    };

    const toggleExportColumn = (columnKey) => {
        setExportColumns(prev => ({
            ...prev,
            [columnKey]: !prev[columnKey]
        }));
    };

    const handleExcelDownload = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams();
            if (searchQuery) queryParams.append('search', searchQuery);
            queryParams.append('limit', '0'); // Get all plans

            const data = await fetchApi(`/plan?${queryParams.toString()}`);
            const plansToExport = data.plans || (Array.isArray(data) ? data : []);

            const worksheetData = plansToExport.map((p, index) => {
                const row = { 'No.': index + 1 };
                if (exportColumns.planId) row['Plan ID'] = p.planId;
                if (exportColumns.planName) row['Name'] = p.planName;
                if (exportColumns.planDurationDays) row['Duration'] = `${p.planDurationDays || 0} Days`;
                if (exportColumns.planPrice) row['Price (₹)'] = p.planPrice || 0;
                if (exportColumns.planDiscount) row['Discount (%)'] = `${p.planDiscount || 0}%`;
                if (exportColumns.finalPrice) row['Final Price (₹)'] = p.finalPrice || 0;
                if (exportColumns.planDescription) row['Description'] = p.planDescription || '';
                if (exportColumns.isActive) row['Status'] = p.isActive ? 'Active' : 'Inactive';
                return row;
            });

            const worksheet = XLSX.utils.json_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Plans');
            XLSX.writeFile(workbook, 'PlanList.xlsx');
        } catch (error) {
            console.error("Excel export error:", error);
            toast.error("Failed to export data");
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams();
            if (searchQuery) queryParams.append('search', searchQuery);
            queryParams.append('limit', '0');

            const data = await fetchApi(`/plan?${queryParams.toString()}`);
            const plansToPrint = data.plans || (Array.isArray(data) ? data : []);

            const printWindow = window.open('', '', 'height=600,width=800');
            printWindow.document.write('<html><head><title>Plans List</title>');
            printWindow.document.write('<style>');
            printWindow.document.write('body { font-family: Arial, sans-serif; margin: 20px; }');
            printWindow.document.write('h1 { text-align: center; color: #0b3a54; }');
            printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }');
            printWindow.document.write('th, td { border: 1px solid #ddd; padding: 5px; text-align: left; }');
            printWindow.document.write('th { background-color: #f0fdf4; color: #0b3a54; font-weight: 600; }');
            printWindow.document.write('</style></head><body>');
            printWindow.document.write('<h1>Plans List</h1>');
            printWindow.document.write('<table><thead><tr><th>No.</th>');
            if (exportColumns.planId) printWindow.document.write('<th>Plan ID</th>');
            if (exportColumns.planName) printWindow.document.write('<th>Name</th>');
            if (exportColumns.planDurationDays) printWindow.document.write('<th>Duration</th>');
            if (exportColumns.planPrice) printWindow.document.write('<th>Price</th>');
            if (exportColumns.planDiscount) printWindow.document.write('<th>Discount</th>');
            if (exportColumns.finalPrice) printWindow.document.write('<th>Final Price</th>');
            if (exportColumns.planDescription) printWindow.document.write('<th>Description</th>');
            if (exportColumns.isActive) printWindow.document.write('<th>Status</th>');
            printWindow.document.write('</tr></thead><tbody>');

            plansToPrint.forEach((p, index) => {
                printWindow.document.write('<tr>');
                printWindow.document.write(`<td>${index + 1}</td>`);
                if (exportColumns.planId) printWindow.document.write(`<td>${p.planId || ''}</td>`);
                if (exportColumns.planName) printWindow.document.write(`<td>${p.planName || ''}</td>`);
                if (exportColumns.planDurationDays) printWindow.document.write(`<td>${p.planDurationDays || 0} Days</td>`);
                if (exportColumns.planPrice) printWindow.document.write(`<td>₹${p.planPrice || 0}</td>`);
                if (exportColumns.planDiscount) printWindow.document.write(`<td>${p.planDiscount || 0}%</td>`);
                if (exportColumns.finalPrice) printWindow.document.write(`<td>₹${p.finalPrice || 0}</td>`);
                if (exportColumns.planDescription) printWindow.document.write(`<td>${p.planDescription || ''}</td>`);
                if (exportColumns.isActive) printWindow.document.write(`<td>${p.isActive ? 'Active' : 'Inactive'}</td>`);
                printWindow.document.write('</tr>');
            });

            printWindow.document.write('</tbody></table></body></html>');
            printWindow.document.close();
            printWindow.print();
        } catch (error) {
            console.error("Print error:", error);
            toast.error("Failed to print data");
        } finally {
            setLoading(false);
        }
    };
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const userRole = JSON.parse(sessionStorage.getItem('user'))?.role;
    const isSuperAdmin = userRole === 'SUPER_ADMIN';
    const canAdd = isSuperAdmin || hasPermission('Plan', 'add');
    const canUpdate = isSuperAdmin || hasPermission('Plan', 'update');
    const canDelete = isSuperAdmin || hasPermission('Plan', 'delete');

    const fetchPlans = async (search = '', page = 1, limit = 10) => {
        try {
            if (!search) setLoading(true);
            const queryParams = new URLSearchParams();
            if (search) queryParams.append('search', search);
            queryParams.append('page', page);
            queryParams.append('limit', limit);

            console.log('Fetching plans from:', `/plan?${queryParams.toString()}`);
            const data = await fetchApi(`/plan?${queryParams.toString()}`);
            console.log('Plans data received:', data);

            if (data && data.plans && Array.isArray(data.plans)) {
                setRows(data.plans);
                setTotalEntries(data.total || 0);
                setTotalPages(data.pages || 1);
            } else if (Array.isArray(data)) {
                setRows(data);
                setTotalEntries(data.length);
                setTotalPages(Math.ceil(data.length / limit));
            } else {
                setRows([]);
                setTotalEntries(0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error('Failed to load plans');
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchPlans(searchQuery, currentPage, itemsPerPage);
        }, 200);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, currentPage, itemsPerPage]);

    const handleEdit = (plan) => {
        if (onEditPlan) {
            onEditPlan(plan);
        }
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this plan?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await fetchApi(`/plan/${id}`, { method: 'DELETE' });
                    Swal.fire('Deleted!', 'Plan has been deleted.', 'success');
                    fetchPlans(searchQuery, currentPage, itemsPerPage);
                } catch (error) {
                    console.error('Error deleting plan:', error);
                    toast.error(error.message || 'Failed to delete plan');
                }
            }
        });
    };

    const handleAdd = () => {
        if (onAddPlan) {
            onAddPlan();
        }
    };

    if (loading) {
        return <PageSkeleton />;
    }

    const columns = [
        { key: 'planId', label: 'PLAN ID' },
        { key: 'planName', label: 'NAME' },
        { key: 'planDurationDays', label: 'DURATION', render: (item) => `${item.planDurationDays} Days` },
        { key: 'planPrice', label: 'PRICE (₹)', render: (item) => `₹${item.planPrice || 0}` },
        { key: 'planDiscount', label: 'DISCOUNT (%)', render: (item) => `${item.planDiscount || 0}%` },
        { key: 'finalPrice', label: 'FINAL PRICE (₹)', render: (item) => <span style={{ color: '#059669', fontWeight: '700' }}>₹{item.finalPrice || 0}</span> },
        { key: 'planDescription', label: 'DESCRIPTION', render: (item) => item.planDescription || '—' },
        {
            key: 'isActive',
            label: 'STATUS',
            render: (item) => (
                <span style={{ color: item.isActive ? '#059669' : '#ef4444', fontWeight: '600' }}>
                    {item.isActive ? 'Active' : 'Inactive'}
                </span>
            )
        },
        ...((canUpdate || canDelete) ? [{
            key: 'actions',
            label: 'ACTIONS',
            render: (item) => (
                <div className="list-page-action-icons">
                    {canUpdate && (
                        <button type="button" className="list-page-icon-btn" title="Edit" onClick={() => handleEdit(item)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                    )}
                    {canDelete && (
                        <button type="button" className="list-page-icon-btn" title="Delete" onClick={() => handleDelete(item._id)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                    )}
                </div>
            )
        }] : [])
    ];

    return (
        <div className="list-page-container">
                        <div className="list-page-header">
                <h1 className="list-page-title">Plans Management</h1>
                <div className="list-page-action-buttons">
                    {(isSuperAdmin || hasPermission('Plan', 'report')) && (
                        <>
                            <button className="list-page-btn list-page-btn-excel" onClick={() => { setExportType('excel'); setIsExportModalOpen(true); }}>EXCEL</button>
                            <button className="list-page-btn list-page-btn-print" onClick={() => { setExportType('print'); setIsExportModalOpen(true); }}>PRINT</button>
                        </>
                    )}
                    {canAdd && (
                        <button className="list-page-btn list-page-btn-add" onClick={handleAdd}>
                            ADD PLAN
                        </button>
                    )}
                </div>
            </div>

            <div className="list-page-toolbar">
                <SearchBar
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                    }}
                    placeholder="Search plans by name..."
                />
            </div>

            <DataTable
                columns={columns}
                data={rows}
                loading={loading}
                emptyMessage="No records found"
                page={currentPage}
                pageSize={itemsPerPage}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setItemsPerPage}
            />

            {isExportModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', width: '500px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h2 style={{ margin: 0, color: '#1e293b', fontSize: '18px', fontWeight: 'bold' }}>Select Columns to {exportType === 'excel' ? 'Export' : 'Print'}</h2>
                            <button onClick={() => { const allKeys = Object.keys(columnLabels); const allChecked = allKeys.every(k => exportColumns[k]); const newExportColumns = { ...exportColumns }; allKeys.forEach(k => newExportColumns[k] = !allChecked); setExportColumns(newExportColumns); }} style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', cursor: 'pointer' }}>{Object.keys(columnLabels).every(k => exportColumns[k]) ? 'Deselect All' : 'Select All'}</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                            {Object.entries(columnLabels).map(([key, label]) => (
                                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333', cursor: 'pointer', padding: '4px' }}>
                                    <input type="checkbox" checked={exportColumns[key]} onChange={() => toggleExportColumn(key)} style={{ accentColor: '#06b6d4', width: '16px', height: '16px' }} />
                                    {label}
                                </label>
                            ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', pt: '10px', borderTop: '1px solid #e2e8f0', marginTop: '10px', paddingTop: '15px' }}>
                            <button onClick={() => setIsExportModalOpen(false)} style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#475569', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                            <button onClick={() => { if (exportType === 'excel') handleExcelDownload(); else handlePrint(); setIsExportModalOpen(false); }} style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', backgroundColor: '#06b6d4', color: 'white', cursor: 'pointer', fontWeight: '600' }}>{exportType === 'excel' ? 'Export Excel' : 'Print'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PlanList;
